// English translations for the core application areas
export const enTranslations = {
  common: {
    // Hero section
    "hero.title": "Control Your Contracts",
    "hero.subtitle": "Simplify contract management with our powerful solution. Get full overview, reduce costs and never miss another expiration.",
    "hero.companyTitle": "Simple Contract Management",
    "hero.companySubtitle": "for small and large businesses.",
    "hero.pricing": "Simple and transparent pricing model. Subtrackr is always free!",
    "hero.benefit1": "Get full control over your contracts at no cost.",
    "hero.benefit2": "No credit card information required.",
    "hero.getStarted": "Get Started",
    "hero.learnMore": "Learn More",
    "hero.neverMiss": "Never miss an expiration",

    // Features section
    "features.title": "Take Control of Your Contracts",
    "features.subtitle": "Our platform provides everything you need to manage contracts efficiently",
    
    "features.contractManagement.title": "Contract Management",
    "features.contractManagement.description": "Store and organize all your contracts in one secure place with easy search and filtering",
    
    "features.reminders.title": "Smart Reminders",
    "features.reminders.description": "Get timely notifications about renewals and termination deadlines to avoid unwanted auto-renewals",
    
    "features.dashboards.title": "Powerful Dashboards",
    "features.dashboards.description": "Gain insights with visual dashboards showing contract status, expenses, and upcoming renewals",
    
    "features.compliance.title": "Compliance Tracking",
    "features.compliance.description": "Keep track of compliance requirements and ensure your business stays compliant with regulations",
    
    "features.notifications.title": "Custom Notifications",
    "features.notifications.description": "Configure notification schedules for your team based on contract events and milestones",
    
    "features.collaboration.title": "Team Collaboration",
    "features.collaboration.description": "Share contracts with team members and assign roles and responsibilities",

    // Testimonials section
    "testimonials.title": "Trusted by Businesses Across Norway",
    "testimonials.subtitle": "See what our customers have to say about Subtrackr",
    
    "testimonials.quote1": "Subtrackr has transformed how we manage contracts. We've cut costs by 15% by catching auto-renewals that we would have missed before.",
    "testimonials.author1": "Maria Johansen",
    "testimonials.role1": "Operations Director",
    "testimonials.company1": "Nordic Solutions AS",
    
    "testimonials.quote2": "The dashboard gives us a complete overview of all contracts across departments. Simple to use and incredibly effective.",
    "testimonials.author2": "Thomas Berg",
    "testimonials.role2": "CFO",
    "testimonials.company2": "Fjord Technology",
    
    "testimonials.quote3": "We've reduced time spent on contract administration by 70%. The reminders alone are worth the investment.",
    "testimonials.author3": "Lisa Andersen",
    "testimonials.role3": "Legal Counsel",
    "testimonials.company3": "Greenscape Energy",
    
    // Pricing section
    "pricing.title": "Simple, Transparent Pricing",
    "pricing.subtitle": "Choose the plan that fits your needs",
    "pricing.perMonth": "/month",
    "pricing.mostPopular": "Most Popular",
    "pricing.question": "Need a custom solution for your business?",
    "pricing.contactUs": "Contact us",
    
    "pricing.starter.name": "Starter",
    "pricing.starter.price": "999 kr",
    "pricing.starter.description": "Perfect for small businesses",
    "pricing.starter.feature1": "Up to 50 contracts",
    "pricing.starter.feature2": "Email notifications",
    "pricing.starter.feature3": "Basic reporting",
    "pricing.starter.feature4": "24/7 support",
    "pricing.starter.button": "Get Started",
    
    "pricing.professional.name": "Professional",
    "pricing.professional.price": "1,999 kr",
    "pricing.professional.description": "Ideal for growing businesses",
    "pricing.professional.feature1": "Unlimited contracts",
    "pricing.professional.feature2": "Advanced dashboards",
    "pricing.professional.feature3": "Team collaboration",
    "pricing.professional.feature4": "Custom notifications",
    "pricing.professional.feature5": "Priority support",
    "pricing.professional.button": "Start Free Trial",
    
    "pricing.enterprise.name": "Enterprise",
    "pricing.enterprise.price": "3,999 kr",
    "pricing.enterprise.description": "For large organizations",
    "pricing.enterprise.feature1": "Unlimited everything",
    "pricing.enterprise.feature2": "Custom integrations",
    "pricing.enterprise.feature3": "Dedicated account manager",
    "pricing.enterprise.feature4": "SLA guarantees",
    "pricing.enterprise.feature5": "Advanced security features",
    "pricing.enterprise.feature6": "On-premises option",
    "pricing.enterprise.button": "Contact Sales",
    
    // CTA section
    "cta.title": "Ready to Transform Your Contract Management?",
    "cta.subtitle": "Join hundreds of satisfied customers who have simplified their contract management with Subtrackr",
    "cta.getStarted": "Get Started Now",
    "cta.contactSales": "Contact Sales",
    
    // Footer
    "footer.description": "Subtrackr helps businesses take control of their contracts with powerful management tools, reminders, and analytics.",
    "footer.product": "Product",
    "footer.features": "Features",
    "footer.pricing": "Pricing",
    "footer.security": "Security",
    "footer.company": "Company",
    "footer.about": "About",
    "footer.contact": "Contact",
    "footer.privacy": "Privacy Policy",
    "footer.terms": "Terms of Service",
    "footer.copyright": "© {{year}} Subtrackr. All rights reserved.",
    
    // Navigation
    "goToDashboard": "Go to Dashboard",
    "login": "Log In",
    "signup": "Sign Up",

    // Timeline translations
    "viewTimeline": "View Timeline",
    "timeline": {
      "title": "Timeline",
      "subtitle": "View the history of changes made to this contract",
      "noChanges": "No changes recorded for this contract yet.",
      "loading": "Loading timeline..."
    },
    
    // Contract Types translations
    contractTypes: {
      subscription: {
        name: "Subscription",
        description: "A recurring contract with regular payments"
      },
      serviceAgreement: {
        name: "Service Agreement",
        description: "Contract for providing services"
      },
      license: {
        name: "License Agreement",
        description: "Contract for the use of intellectual property"
      },
      onetime: {
        name: "One-time Purchase",
        description: "Single purchase without renewal"
      },
      lease: {
        name: "Lease/Rental",
        description: "Contract for renting/leasing property or equipment"
      },
      sales: {
        name: "Sales Contract",
        description: "Contract for selling products or services"
      },
      employment: {
        name: "Employment Contract",
        description: "Contract with employees"
      },
      intro: "Contract types help categorize your agreements and apply consistent rules. Each type can have specific renewal settings and direction/purpose.",
      empty: "You don't have any contract types yet. Add default types or create your own.",
      addingDefaults: "Adding defaults...",
      addDefaults: "Add Default Contract Types",
      createManually: "Create Type Manually",
      yourTypes: "Your Contract Types",
      count: "You have {{count}} contract type(s)"
    },

    // Categories translations
    categories: {
      software: {
        name: "Software",
        description: "Software licenses and subscriptions"
      },
      hardware: {
        name: "Hardware",
        description: "Physical hardware and equipment"
      },
      services: {
        name: "Services",
        description: "Professional and consulting services"
      },
      utilities: {
        name: "Utilities",
        description: "Utilities like electricity, water, etc."
      },
      rent: {
        name: "Rent",
        description: "Office and workspace rentals"
      },
      intro: "Categories help you organize your contracts and filter them more effectively. Add some categories that match your organization's needs.",
      noCategories: "No Categories Yet",
      empty: "You haven't created any categories yet. Add some to help organize your contracts.",
      addDefaults: "Add Default Categories",
      createCustom: "Create Custom Category"
    }
  }
};
